<template>
  <div>
    <client-only>
      <Teleport to="body" :disabled="!shouldPortal">
        <transition name="fade">
          <div
            class="modal-backdrop"
            @click="$emit('close')"
            v-if="isVisible || shouldPersistInDom"
            v-show="isVisible"
            :style="cssVar"
          />
        </transition>
        <transition :name="transitionName">
          <div
            :class="['modal', className]"
            ref="modal"
            v-if="isVisible || shouldPersistInDom"
            v-show="isVisible"
            v-bind="$attrs"
          >
            <div
              :class="{
                'modal-container': true,
                'content-overflowing':
                  (state.isContentElementOverflowingTop && showOverFlow) ||
                  (state.isContentElementOverflowingBottom && showOverFlow),
                'content-overflowing-top': state.isContentElementOverflowingTop && showOverFlow,
                'content-overflowing-bottom': state.isContentElementOverflowingBottom && showOverFlow
              }"
              ref="modal-content"
            >
              <header class="modal-header weight-700" v-if="hasSlot(slots, 'header')">
                <slot name="header" />
                <slot name="closeButton" />
              </header>
              <div
                :class="{
                  'modal-content': true,
                  overflowing:
                    (state.isContentElementOverflowingTop && showOverFlow) ||
                    (state.isContentElementOverflowingBottom && showOverFlow),
                  'overflowing-top': state.isContentElementOverflowingTop && showOverFlow,
                  'overflowing-bottom': state.isContentElementOverflowingBottom && showOverFlow
                }"
                v-if="hasSlot(slots, 'content')"
                :id="contentElementId"
              >
                <slot name="content" />
              </div>
              <slot />
              <footer class="modal-footer">
                <slot name="footer" />
              </footer>
            </div>
          </div>
        </transition>
      </Teleport>
    </client-only>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, watch, onMounted, onUnmounted, nextTick } from 'vue';
import uniqueId from 'lodash-es/uniqueId';
import debounce from 'lodash-es/debounce';
import ResizeObserverPolyfill from 'resize-observer-polyfill';
import { hasSlot } from '@/resources/helpers';
import { useSlots } from 'vue';
// export default defineComponent({
//   name: 'Modal',
const props = defineProps({
  className: {
    type: String
  },
  isVisible: {
    type: Boolean,
    default: false
  },
  shouldPersistInDom: {
    type: Boolean,
    default: false
  },
  transitionName: {
    type: String,
    default: 'fade-in-down'
  },
  shouldPortal: {
    type: Boolean,
    default: true
  },
  overLayPercent: {
    type: Number,
    default: 0.4
  },
  showOverFlow: {
    type: Boolean,
    default: true
  }
});

defineOptions({
  inheritAttrs: false
});

defineEmits(['close']);

const slots = useSlots();
const state = reactive({
  isContentElementOverflowingTop: false,
  isContentElementOverflowingBottom: false
});

const cssVar = {
  '--overlay-percent': props.overLayPercent
};

const checkContentElementOverflow = (element) => {
  const hasOverflow = element.offsetHeight < element.scrollHeight;

  let hasOverflowAtTop = false;
  let hasOverflowAtBottom = false;

  if (hasOverflow) {
    hasOverflowAtTop = element.scrollTop > 10;
    hasOverflowAtBottom = element.scrollTop < element.scrollHeight - element.offsetHeight - 10;
  }

  state.isContentElementOverflowingTop = hasOverflowAtTop;
  state.isContentElementOverflowingBottom = hasOverflowAtBottom;
};

const contentElementId = ref('modalContent_' + uniqueId());
let contentElementResizeObserver = null;

const debouncedContentElementScrollEventHandler = debounce((event) => {
  checkContentElementOverflow(event.target);
}, 200);

onMounted(() => {
  contentElementResizeObserver = new ResizeObserverPolyfill((entries) => {
    entries.forEach((entry) => {
      const element = entry.target;
      checkContentElementOverflow(element);
    });
  });
});

const addContentElementObservers = () => {
  const element = document.querySelector('#' + contentElementId.value);

  if (element) {
    contentElementResizeObserver.observe(element);
    element.addEventListener('scroll', debouncedContentElementScrollEventHandler, {
      passive: true
    });
  }
};

const removeContentElementObservers = () => {
  const element = document.querySelector('#' + contentElementId.value);

  if (element) {
    contentElementResizeObserver.disconnect();
    element.removeEventListener('scroll', debouncedContentElementScrollEventHandler);
  }
};

onUnmounted(() => {
  removeContentElementObservers();
});

watch(
  () => props.isVisible,
  async (newVal) => {
    if (!newVal) {
      removeContentElementObservers();
      return;
    }

    // https://portal-vue.linusb.org/guide/caveats.html#refs
    await nextTick();
    await nextTick();

    addContentElementObservers();
  },
  {
    inheritAttrs: false
  }
);
</script>

<style lang="scss" scoped>
@use '@/assets/styles/css_BASE/helpers/mixins/_bp.scss' as *;

$z-index-modal: 1001;

.modal {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: auto;
  pointer-events: none;
  z-index: $z-index-modal;

  .modal-container {
    pointer-events: all;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 945px;
    margin: 0 auto;
    max-width: 100%;
    max-height: 100%;
    min-height: 100%;
    min-width: 100%;
    z-index: $z-index-modal + 1;

    @include bp(sm-up) {
      min-height: 0;
      min-width: 0;
    }
  }

  .modal-header {
    position: relative;

    > * {
      margin: 0;
    }
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .modal-close {
    cursor: pointer;
  }
}

.modal-backdrop {
  background-color: rgba(0, 0, 0, var(--overlay-percent));
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 97;
}
</style>
